// src/portals/admin/components/AdminDataTable.js
import React, { useState, useMemo } from "react";
import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import { ChevronLeft, ChevronRight, Search, Filter, ArrowUp, ArrowDown } from "lucide-react";
import Input from "../../../components/ui/Input"; // Assuming basic Input component exists

const AdminDataTable = ({ 
  title, 
  columns, 
  data, 
  searchableFields = [], 
  filterableFields = [] // Array of { key: 'status', label: 'Status', options: ['Active', 'Inactive'] }
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Handle Sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and Sort Data
  const processedData = useMemo(() => {
    let result = [...data];

    // 1. Search
    if (searchTerm) {
      result = result.filter(item => 
        searchableFields.some(field => 
          String(item[field]).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // 2. Filter
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        result = result.filter(item => item[key] === filters[key]);
      }
    });

    // 3. Sort
    if (sortConfig.key) {
      result.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
        if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, filters, sortConfig, searchableFields]);

  // Pagination
  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <Card>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        {title && (
          <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
          {/* Search Input */}
          {searchableFields.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 w-full sm:w-64"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          )}

          {/* Filters */}
          {filterableFields.map((filter) => (
            <div key={filter.key} className="relative">
              <select
                className="pl-3 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 appearance-none bg-white cursor-pointer"
                value={filters[filter.key] || ""}
                onChange={(e) => setFilters({ ...filters, [filter.key]: e.target.value })}
              >
                <option value="">All {filter.label}</option>
                {filter.options.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              <Filter className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 pointer-events-none" size={14} />
            </div>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`p-3 text-left font-semibold text-slate-600 whitespace-nowrap ${col.sortable ? 'cursor-pointer hover:bg-slate-100 select-none' : ''}`}
                  style={col.width ? { width: col.width, minWidth: col.width } : {}}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div className="flex items-center gap-1">
                    {col.header}
                    {col.sortable && (
                      <div className="flex flex-col">
                        <ArrowUp size={10} className={sortConfig.key === col.key && sortConfig.direction === 'asc' ? 'text-purple-600' : 'text-slate-300'} />
                        <ArrowDown size={10} className={sortConfig.key === col.key && sortConfig.direction === 'desc' ? 'text-purple-600' : 'text-slate-300'} />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {paginatedData.length > 0 ? (
              paginatedData.map((row) => (
                <tr key={row.id}>
                  {columns.map((col) => (
                    <td
                      key={`${row.id}-${col.key}`}
                      className="p-3 text-slate-700 align-top"
                      style={col.width ? { width: col.width, minWidth: col.width } : {}}
                    >
                      {col.render ? col.render(row) : row[col.key]}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="p-8 text-center text-slate-500">
                  No results found matching your criteria.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Pagination */}
      <div className="flex justify-between items-center mt-4 text-sm text-slate-600">
        <p>
          Showing {Math.min((currentPage - 1) * itemsPerPage + 1, processedData.length)} to {Math.min(currentPage * itemsPerPage, processedData.length)} of {processedData.length} results
        </p>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          >
            <ChevronLeft size={16} />
          </Button>
          <span className="px-2">Page {currentPage} of {totalPages || 1}</span>
          <Button 
            variant="ghost" 
            size="sm" 
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default AdminDataTable;
